import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Ruler, List } from 'lucide-react';
import type { DimensionToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useDimensionsList, useDimensionMutations, useDimensionTypes } from '~/hooks/use-dimensions';
import { tokenToForm, type DimensionFormValues } from '~/lib/schemas/dimension';
import { DimensionEditDialog } from './dimension-edit-dialog';
import { DimensionValuesSheet } from './values-sheet';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Dimensions (Διαστάσεις) CRUD surface + per-row values manager — parity with the legacy grid + values modal. */
export function DimensionsGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useDimensionsList();
	const { data: types } = useDimensionTypes();
	const { remove } = useDimensionMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<DimensionFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<DimensionToken | null>(null);
	const [valuesFor, setValuesFor] = useState<DimensionToken | null>(null);

	const typeName = useMemo(() => {
		const map = new Map<number, string>();
		for (const ty of types ?? []) if (ty.id != null) map.set(ty.id, ty.nm ?? '');
		return (id?: number) => (id != null ? (map.get(id) ?? '') : '');
	}, [types]);

	const columns = useMemo<SystemGridColumn<DimensionToken>[]>(
		() => [
			{
				key: 'name',
				header: t('attributes.dimensions.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'type',
				header: t('attributes.dimensions.columns.type'),
				width: '180px',
				sortValue: o => typeName(o.dt as number | undefined),
				exportValue: o => typeName(o.dt as number | undefined),
				render: o => <span className="truncate text-sm text-foreground-muted">{typeName(o.dt as number | undefined) || '—'}</span>
			},
			{
				key: 'values',
				header: '',
				width: '130px',
				render: o => (
					<Button
						variant="outline"
						size="sm"
						onClick={e => {
							e.stopPropagation();
							setValuesFor(o);
						}}>
						<List size={14} /> {t('attributes.dimensions.values')}
					</Button>
				)
			},
			{
				key: 'visible',
				header: t('attributes.dimensions.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.iv)} />
			},
			{
				key: 'sort',
				header: t('attributes.dimensions.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: o => o.si ?? 0,
				exportValue: o => o.si ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.si}</span>
			}
		],
		[t, typeName]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<DimensionToken>
				gridId="dimensions"
				title={t('attributes.dimensions.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.nm}
				columns={columns}
				searchPredicate={(o, q) => (o.nm ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Ruler size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('attributes.dimensions.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="dimensions"
			/>

			<DimensionEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<DimensionValuesSheet
				open={Boolean(valuesFor)}
				onOpenChange={o => !o && setValuesFor(null)}
				dimensionId={valuesFor?.id ?? null}
				dimensionName={valuesFor?.nm ?? ''}
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('attributes.dimensions.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('attributes.dimensions.deleteBody', { name: deleteRow?.nm || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
